/**
 * Rich text for the native `text` widget (`options.html`). The admin editor writes it and the
 * player renders it with innerHTML, so both sides run it through this one allowlist: a handful of
 * inline formatting tags, lists and line breaks, and a few style properties with checked values.
 * Anything else is unwrapped (its text kept) or, for script-like elements, dropped with its content.
 *
 * Font sizes are kept in `em` so they scale with the widget's base size, which the player derives
 * from the layout (see the runtime's `textFontSize`).
 */

/** The longest `options.html` the editor writes and the schema accepts. */
export const RICH_TEXT_MAX_LENGTH = 20_000;

/** `<font size="1…7">` (what execCommand('fontSize') writes) as em, 3 = the widget's base size. */
const FONT_SIZE_EM = ['0.6em', '0.8em', '1em', '1.25em', '1.5em', '2em', '3em'] as const;

const KEEP = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'BR', 'DIV', 'SPAN', 'UL', 'OL', 'LI']);
/** Elements whose content is never text worth keeping. */
const DROP = new Set([
  'SCRIPT', 'STYLE', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'SVG', 'MATH',
  'IMG', 'VIDEO', 'AUDIO', 'CANVAS', 'LINK', 'META', 'HEAD', 'TITLE', 'INPUT', 'TEXTAREA', 'SELECT', 'BUTTON',
]);

const COLOR = /^(#[0-9a-f]{3,8}|rgba?\(\s*[\d.\s,%]+\)|[a-z]{3,20})$/i;
const EM = /^(\d{1,2}(\.\d{1,3})?)em$/;
const ALIGN = new Set(['left', 'center', 'right', 'justify']);
const WEIGHT = new Set(['normal', 'bold', '400', '700']);
const FONT_STYLE = new Set(['normal', 'italic']);
const DECORATION = new Set(['none', 'underline', 'line-through', 'underline line-through']);

function safeStyle(prop: string, value: string): string | undefined {
  const v = value.trim().toLowerCase();
  switch (prop) {
    case 'color':
    case 'background-color':
      return COLOR.test(v) && !v.includes('expression') ? v : undefined;
    case 'font-size': {
      const m = EM.exec(v);
      if (!m) return undefined;
      const em = Math.min(4, Math.max(0.25, Number(m[1])));
      return `${em}em`;
    }
    case 'text-align':
      return ALIGN.has(v) ? v : undefined;
    case 'font-weight':
      return WEIGHT.has(v) ? v : undefined;
    case 'font-style':
      return FONT_STYLE.has(v) ? v : undefined;
    case 'text-decoration':
    case 'text-decoration-line':
      return DECORATION.has(v) ? v : undefined;
    default:
      return undefined;
  }
}

function copyStyles(from: Element, to: HTMLElement): void {
  const style = (from as HTMLElement).style as CSSStyleDeclaration | undefined;
  if (style) {
    for (let i = 0; i < style.length; i++) {
      const prop = style.item(i);
      const value = safeStyle(prop, style.getPropertyValue(prop));
      if (value) to.style.setProperty(prop === 'text-decoration-line' ? 'text-decoration' : prop, value);
    }
  }
  const align = from.getAttribute('align');
  if (align && ALIGN.has(align.toLowerCase())) to.style.setProperty('text-align', align.toLowerCase());
  if (from.tagName === 'FONT') {
    const color = from.getAttribute('color');
    const value = color ? safeStyle('color', color) : undefined;
    if (value) to.style.setProperty('color', value);
    const size = Number(from.getAttribute('size'));
    const em = Number.isInteger(size) ? FONT_SIZE_EM[Math.min(7, Math.max(1, size)) - 1] : undefined;
    if (em) to.style.setProperty('font-size', em);
  }
}

function cleanChildren(from: Node, to: Node, doc: Document): void {
  for (const child of Array.from(from.childNodes)) {
    if (child.nodeType === 3) {
      to.appendChild(doc.createTextNode(child.textContent ?? ''));
      continue;
    }
    if (child.nodeType !== 1) continue; // comments, processing instructions
    const el = child as Element;
    const tag = el.tagName.toUpperCase();
    if (DROP.has(tag)) continue;
    const name = KEEP.has(tag) ? tag : tag === 'P' ? 'DIV' : tag === 'FONT' ? 'SPAN' : undefined;
    if (!name) {
      cleanChildren(el, to, doc); // unknown wrapper: keep what it says, not what it is
      continue;
    }
    const out = doc.createElement(name.toLowerCase());
    if (name !== 'BR') {
      copyStyles(el, out);
      cleanChildren(el, out, doc);
    }
    // A <span>/<font> that carried nothing allowed is just noise.
    if (name === 'SPAN' && !out.getAttribute('style')) {
      while (out.firstChild) to.appendChild(out.firstChild);
      continue;
    }
    to.appendChild(out);
  }
}

/** Parses into an inert <template>: nothing in it runs or loads. */
function parse(html: string): DocumentFragment {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
}

/** The allowlisted subset of `html`, safe to assign to innerHTML. Needs a DOM. */
export function sanitizeRichText(html: string): string {
  const out = document.createElement('div');
  cleanChildren(parse(html), out, document);
  return out.innerHTML;
}

/** Plain text of rich text: line breaks for blocks and <br>, for summaries and the `text` fallback. */
export function richTextToPlain(html: string): string {
  const lines: string[] = [''];
  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) lines[lines.length - 1] += child.textContent ?? '';
      else if (child.nodeType === 1) {
        const tag = (child as Element).tagName.toUpperCase();
        if (DROP.has(tag)) continue;
        if (tag === 'BR') {
          lines.push('');
          continue;
        }
        const block = tag === 'DIV' || tag === 'P' || tag === 'LI';
        if (block && lines[lines.length - 1] !== '') lines.push('');
        walk(child);
        if (block && lines[lines.length - 1] !== '') lines.push('');
      }
    }
  };
  walk(parse(html));
  return lines.join('\n').trim();
}

/** Escapes plain text into rich text (newlines → <br>), to start the editor from a plain `text`. */
export function plainToRichText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}
